import {
  DatePickerValue,
  DropdownButton,
  PresetSingle,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import moment, { Moment } from 'moment-timezone';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { appActions } from 'app/app.actions';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { notificationActions } from 'components/notifications/actions/notification';
import { useShortHumanReadableTimer } from 'hooks/useShortHumanReadableTimer';
import { useCommandHandler } from 'utils/commands';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { ConversationFollowUpQueueSelectModal } from './components/conversationFollowUpQueueSelectModal/conversationFollowUpQueueSelectModal';
import { ConversationFollowUpDatePicker } from './components/datepicker/conversationFollowUpDatePicker';
import styles from './conversationFollowUp.module.scss';
import { useNextQuarter } from './utils/useNextQuarter';

export const ConversationFollowUp = memo(
  function ConversationFollowUp(): JSX.Element {
    const {
      direction,
      id: csid = null,
      status,
      queue,
      assignment,
      pending_expiration_at: pendingExpirationAt,
    } = useAppSelector(
      (state) => state.conversation.metaConversation.conversation,
    );
    const dispatch = useAppDispatch();
    const followUpLocalTime = useMemo<Moment | null>(
      () =>
        pendingExpirationAt ? moment(pendingExpirationAt).utc().local() : null,
      [pendingExpirationAt],
    );
    const relativeFollowUpTime = useShortHumanReadableTimer(followUpLocalTime);
    const shouldShowRemoveFollowup =
      pendingExpirationAt &&
      (status === 'pending' || status === 'awaitingpending');

    const nextQuarter = useNextQuarter();

    const followUpLocalTimeOrNextQuarter = useMemo(
      () => followUpLocalTime || nextQuarter,
      [followUpLocalTime, nextQuarter],
    );

    const [selectedDate, setSelectedDate] = useState<Moment | null>(
      followUpLocalTimeOrNextQuarter,
    );
    const dropdownButtonRef = useRef<DropdownButton | null>(null);

    const isPresetsEnabled = useBreakpointValue({ base: false, md: true });

    useCommandHandler({
      name: 'conversation:followUp',
      actionFn: () => {
        dropdownButtonRef.current?.handleDropdownToggle();
      },
    });

    useEffect(() => {
      setSelectedDate(followUpLocalTimeOrNextQuarter);
    }, [followUpLocalTimeOrNextQuarter]);

    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = useCallback((newIsOpen: boolean) => {
      setIsOpen(newIsOpen);

      if (newIsOpen) {
        ampli.conversationFollowUpMenuOpened();
      }
    }, []);

    const presets: PresetSingle[] = [
      {
        key: '1_hour',
        label: 'Remind me in 1 hour',
        date: moment().add(1, 'hour'),
      },
      {
        key: '3_hours',
        label: 'Remind me in 3 hours',
        date: moment().add(3, 'hours'),
      },
      {
        key: '24_hours',
        label: 'Remind me in 24 hours',
        date: moment().add(24, 'hours'),
      },
      {
        key: '48_hours',
        label: 'Remind me in 48 hours',
        date: moment().add(48, 'hours'),
      },
    ];

    const performFollowUp = useCallback(
      async (csid: number, date: Moment | null) => {
        if ((date?.diff(moment()) ?? 0) < 0) {
          dispatch(
            notificationActions.notificationAdd({
              text: `You should not select a time that has passed.`,
              title: 'Invalid time',
              showDesktop: true,
              icon: 'exclamation',
              status: 'danger',
              time: 5000,
            }),
          );
          return;
        }

        const timestamp = date?.utc().format() ?? null;

        if (direction === 'outbound' && !queue?.id) {
          const { queueId } = await dispatch(
            appActions.showModal({
              modal: { type: 'conversation:followUpQueueSelect' },
            }),
          );

          ampli.conversationFollowUp({ conversationId: csid });

          dispatch(
            conversationActions.conversationToPending({
              csid,
              timestamp,
              queueId,
            }),
          );
        } else {
          ampli.conversationFollowUp({ conversationId: csid });

          dispatch(
            conversationActions.conversationToPending({
              csid,
              timestamp,
              ...(direction === 'outbound' && { queueId: queue?.id }),
            }),
          );
        }
      },
      [direction, dispatch, queue?.id],
    );

    const handleDateChange = useCallback(
      ({ date, presetKey }: DatePickerValue) => {
        ampli.conversationFollowUpMenuOptionClicked({
          option: presetKey ?? 'date',
        });

        setSelectedDate(date);

        // If a preset is not selected, wait for confirm button
        if (!presetKey || !csid) {
          return;
        }

        performFollowUp(csid, date);
      },
      [csid, performFollowUp],
    );

    const handleConfirm = useCallback(() => {
      ampli.conversationFollowUpMenuOptionClicked({
        option: 'confirm',
      });

      if (!csid) {
        return;
      }

      performFollowUp(csid, selectedDate);
    }, [csid, performFollowUp, selectedDate]);

    const handleCancelFollowUp = useCallback(() => {
      ampli.conversationFollowUpMenuOptionClicked({
        option: 'cancel',
      });

      dispatch(conversationActions.conversationCancelPending({ csid }));
    }, [csid, dispatch]);

    const tooltipContent = (
      <CommandKeybindingsTooltipContent commandName="conversation:followUp">
        {followUpLocalTime ? 'Reschedule follow up' : 'Schedule a follow up'}
      </CommandKeybindingsTooltipContent>
    );

    return (
      <>
        <Tooltip content={tooltipContent} disabled={assignment === null}>
          <DropdownButton
            ref={dropdownButtonRef}
            data-testid="conversation-follow-up-dropdown"
            icon="calendar"
            buttonProps={{
              disabled: assignment === null,
              variant: 'secondary',
            }}
            onToggle={handleToggle}
            dropdownProps={{
              className: styles.dropdown,
              autoFocus: true,
            }}
            dropdown={
              isOpen && (
                <ConversationFollowUpDatePicker
                  date={selectedDate}
                  presets={isPresetsEnabled ? presets : undefined}
                  onChange={handleDateChange}
                  onConfirm={handleConfirm}
                  onCancel={
                    shouldShowRemoveFollowup ? handleCancelFollowUp : undefined
                  }
                />
              )
            }
          >
            {followUpLocalTime
              ? `Follow up ${relativeFollowUpTime}`
              : 'Follow up'}
          </DropdownButton>
        </Tooltip>

        <ConversationFollowUpQueueSelectModal />
      </>
    );
  },
);
